export interface ExtractedEntry {
  key: string;
  label: string;
  value: string;
}

export interface SelectExtractedEntriesOptions {
  filterKeys?: (key: string) => boolean;
  sortOrder?: string[];
  hideEmpty?: boolean;
}

export function getExtractedDataEntries(
  data: Record<string, unknown>
): ExtractedEntry[] {
  const entries: ExtractedEntry[] = [];

  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return entries;
  }

  for (const [key, value] of Object.entries(data)) {
    if (key.toLowerCase() === "success") continue;

    if (value != null && typeof value === "object" && !Array.isArray(value)) {
      for (const [nestedKey, nestedVal] of Object.entries(
        value as Record<string, unknown>
      )) {
        entries.push({
          key: `${key}.${nestedKey}`,
          label: nestedKey.replace(/_/g, " "),
          value: String(nestedVal ?? "—"),
        });
      }
    } else {
      entries.push({
        key,
        label: key.replace(/_/g, " "),
        value: String(value ?? "—"),
      });
    }
  }

  entries.sort((a, b) => {
    const aIsVehicle = a.key
      .toLowerCase()
      .replace(/_/g, " ")
      .includes("vehicle number");
    const bIsVehicle = b.key
      .toLowerCase()
      .replace(/_/g, " ")
      .includes("vehicle number");
    if (aIsVehicle && !bIsVehicle) return -1;
    if (!aIsVehicle && bIsVehicle) return 1;

    const aIsWeight = a.key.toLowerCase().includes("weight");
    const bIsWeight = b.key.toLowerCase().includes("weight");
    if (aIsWeight && !bIsWeight) return -1;
    if (!aIsWeight && bIsWeight) return 1;

    return a.label.localeCompare(b.label);
  });

  return entries;
}

const isEmptyValue = (value: string) =>
  !value || value === "—" || value.trim() === "";

export function selectExtractedEntries(
  data: Record<string, unknown>,
  { filterKeys, sortOrder, hideEmpty }: SelectExtractedEntriesOptions = {}
): ExtractedEntry[] {
  let entries = getExtractedDataEntries(data);

  if (filterKeys) entries = entries.filter((e) => filterKeys(e.key));
  if (hideEmpty) entries = entries.filter((e) => !isEmptyValue(e.value));

  if (sortOrder) {
    const positionOf = (key: string) => {
      const index = sortOrder.findIndex((s) =>
        key.toLowerCase().includes(s.toLowerCase())
      );
      return index === -1 ? sortOrder.length : index;
    };
    entries = [...entries].sort((a, b) => positionOf(a.key) - positionOf(b.key));
  }

  return entries;
}
